import React, { useCallback, useEffect, useState } from 'react'
import { Button } from '@duck/ui'
import { RelativeDateTime } from '../datetime/RelativeDateTime'
import { TranslationKey } from '@/modules'
import { useTar } from '@/modules/i18n'

export type HealthStatus = 'healthy' | 'down' | 'loading'

export interface ServiceHealthStatus {
    name: TranslationKey
    status: HealthStatus
    endpoint: string
    responseTime?: number
    lastChecked?: string
    error?: string
    nodeEnv?: string
    deploymentTag?: string
    version?: string
}

interface ServiceHealthCardProps {
    title: string
    healthStatusDisplayMap: Record<HealthStatus, TranslationKey>
    breakDeploymentTag: (deploymentTag?: string) => string | null
}

const environment = import.meta.env.VITE_NODE_ENV

const environmentUrl = environment === 'development' ? '' : environment === 'staging' ? 'https://staging.xp-lab.com' : 'https://www.xp-lab.com'


interface HealthCheckResponse {
    status: string
    service?: string
    version?: string
    nodeEnv?: string
    deploymentTag?: string
    timestamp?: string
}

export const createServices = (): ServiceHealthStatus[] => [
    {
        name: 'admin.challengeService',
        status: 'loading',
        endpoint: `${environmentUrl}/api/challenges/health`,
    },
    {
        name: 'admin.taskService',
        status: 'loading',
        endpoint: `${environmentUrl}/api/tasks/health`,
    },
    {
        name: 'admin.userService',
        status: 'loading',
        endpoint: `${environmentUrl}/api/users/health`,
    },
    {
        name: 'admin.feedbackService',
        status: 'loading',
        endpoint: `${environmentUrl}/api/feedback/health`,
    },
    {
        name: 'admin.partnerService',
        status: 'loading',
        endpoint: `${environmentUrl}/api/partners/health`,
    },
]

export const checkServiceHealth = async (
    service: ServiceHealthStatus,
): Promise<ServiceHealthStatus> => {
    const startTime = Date.now()

    try {
        const response = await fetch(service.endpoint, {
            method: 'GET',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
        })

        const responseTime = Date.now() - startTime

        if (!response.ok) {
            return {
                ...service,
                status: 'down',
                error: `HTTP ${response.status}: ${response.statusText}`,
                responseTime,
                lastChecked: new Date().toISOString(),
            }
        }

        const healthData: HealthCheckResponse = await response.json()

        return {
            ...service,
            status: healthData.status === 'healthy' ? 'healthy' : 'down',
            error: healthData.status === 'down' ? service.error : undefined,
            responseTime,
            lastChecked: new Date().toISOString(),
            nodeEnv: healthData.nodeEnv,
            deploymentTag: healthData.deploymentTag,
            version: healthData.version,
        }
    } catch (error) {
        const responseTime = Date.now() - startTime

        return {
            ...service,
            status: 'down',
            error: error instanceof Error ? error.message : 'Unknown error',
            responseTime,
            lastChecked: new Date().toISOString(),
        }
    }
}

export const checkAllServicesHealth = async (
    services: ServiceHealthStatus[],
): Promise<ServiceHealthStatus[]> => {
    return Promise.all(services.map(checkServiceHealth))
}

const statusIconMap: Record<
    HealthStatus,
    { color: string; symbol: string }
> = {
    healthy: { color: 'text-success', symbol: '✓' },
    down: { color: 'text-error', symbol: '✗' },
    loading: { color: 'text-accent', symbol: '○' },
}

export const ServicesHealth: React.FC<ServiceHealthCardProps> = ({
    title,
    healthStatusDisplayMap,
    breakDeploymentTag,
}) => {
    const { t } = useTar()
    const [services, setServices] = useState<ServiceHealthStatus[]>(() => createServices())
    const refreshServices = useCallback(async () => {
        setServices((currentServices) => {
            checkAllServicesHealth(currentServices).then(setServices)
            return currentServices
        })
    }, [])

    useEffect(() => {
        refreshServices()
        const interval = setInterval(refreshServices, 25_000)
        return () => clearInterval(interval)
    }, [refreshServices])

    return (
        <div className="bg-card shadow rounded-lg border border-border">
            <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg leading-6 font-medium text-foreground">
                        {title} ({environment})
                    </h3>
                    <Button
                        onClick={refreshServices}
                        variant="outline"
                        size="sm"
                        className="text-indigo-700"
                    >
                        {t('admin.refresh')}
                    </Button>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                    {services.map((service, index) => {
                        const statusIcon = statusIconMap[service.status]

                        return (
                            <div
                                key={`${service.name}-${index}`}
                                className="bg-background p-4 rounded-lg border border-border"
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <h4 className="text-sm font-medium text-foreground">
                                        {t(service.name)}
                                    </h4>
                                    <span className={`text-xl ${statusIcon.color}`}>
                                        {statusIcon.symbol}
                                    </span>
                                </div>

                                <div className="space-y-1 text-xs text-muted-foreground">
                                    <div className="flex justify-between">
                                        <span>{t('admin.status')}:</span>
                                        <span className={`font-medium ${statusIcon.color}`}>
                                            {t(healthStatusDisplayMap[service.status])}
                                        </span>
                                    </div>

                                    {service.responseTime && (
                                        <div className="flex justify-between">
                                            <span>{t('admin.responseTime')}:</span>
                                            <span className="font-medium">
                                                {service.responseTime}
                                                ms
                                            </span>
                                        </div>
                                    )}

                                    {service.lastChecked && (
                                        <div className="flex justify-between">
                                            <span>{t('admin.lastChecked')}:</span>
                                            <span className="font-medium">
                                                <RelativeDateTime isoDateString={service.lastChecked} />
                                            </span>
                                        </div>
                                    )}

                                    {service.version && (
                                        <div className="flex justify-between">
                                            <span>{t('admin.version')}:</span>
                                            <span className="font-medium">{service.version}</span>
                                        </div>
                                    )}

                                    {service.nodeEnv && (
                                        <div className="flex justify-between">
                                            <span>{t('admin.nodeEnv')}:</span>
                                            <span className="font-medium">{service.nodeEnv}</span>
                                        </div>
                                    )}

                                    <div className="flex justify-between">
                                        <span>{t('admin.lastDeployment')}:</span>
                                        <span className="font-medium text-indigo-600">
                                            <RelativeDateTime
                                                isoDateString={breakDeploymentTag(service.deploymentTag)}
                                                fallbackText="local-env"
                                            />
                                        </span>
                                    </div>

                                    {service.error && (
                                        <div className="mt-2 p-2 bg-error/10 border border-error/20 rounded-lg">
                                            <p className="text-error text-xs mt-1">{service.error}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>
        </div>
    )
}